import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, LogOut, Image as ImageIcon, MapPin, Settings as SettingsIcon, Home as HomeIcon, Archive as ArchiveIcon, Share2, Users, Puzzle, Tag, Inbox, AlertTriangle, AlertOctagon, HelpCircle, BarChart3, Database, Activity, Megaphone, FolderOpen, Camera, CreditCard } from "lucide-react";
import GalleryManager from "@/pages/admin/GalleryManager";
import MapManager from "@/pages/admin/MapManager";
import ModSettings from "@/pages/admin/ModSettings";
import ArchiveManager from "@/pages/admin/ArchiveManager";
import SocialManager from "@/pages/admin/SocialManager";
import TeamManager from "@/pages/admin/TeamManager";
import AddonsManager from "@/pages/admin/AddonsManager";
import PoiCategoriesManager from "@/pages/admin/PoiCategoriesManager";
import ReportsManager from "@/pages/admin/ReportsManager";
import FAQManager from "@/pages/admin/FAQManager";
import AnnouncementsManager from "@/pages/admin/AnnouncementsManager";
import StatsManager from "@/pages/admin/StatsManager";
import PlateStatsManager from "@/pages/admin/PlateStatsManager";
import ToolsManager from "@/pages/admin/ToolsManager";
import SystemMonitor from "@/pages/admin/SystemMonitor";
import MediaManager from "@/pages/admin/MediaManager";
import CommunityScreenshotsManager from "@/pages/admin/CommunityScreenshotsManager";
import useBrand from "@/hooks/useBrand";

const TAB_GROUPS = [
    {
        id: "analiza",
        label: "Analiza",
        tabs: [
            { id: "stats", label: "Statystyki", icon: BarChart3 },
            { id: "plate-stats", label: "Tablice", icon: CreditCard },
            { id: "reports", label: "Zgłoszenia", icon: Inbox },
        ],
    },
    {
        id: "tresc",
        label: "Treść",
        tabs: [
            { id: "gallery", label: "Galeria", icon: ImageIcon },
            { id: "map", label: "Mapa & POI", icon: MapPin },
            { id: "poi-categories", label: "Kategorie POI", icon: Tag },
            { id: "addons", label: "Dodatki", icon: Puzzle },
            { id: "archive", label: "Archiwum", icon: ArchiveIcon },
            { id: "team", label: "Zespół", icon: Users },
            { id: "faq", label: "FAQ", icon: HelpCircle },
            { id: "community", label: "Community screens", icon: Camera },
        ],
    },
    {
        id: "komunikacja",
        label: "Komunikacja",
        tabs: [
            { id: "announcements", label: "Ogłoszenia", icon: Megaphone },
            { id: "social", label: "Social & wsparcie", icon: Share2 },
        ],
    },
    {
        id: "system",
        label: "System",
        tabs: [
            { id: "monitor", label: "Monitor", icon: Activity },
            { id: "settings", label: "Ustawienia moda", icon: SettingsIcon },
            { id: "media", label: "Menedżer mediów", icon: FolderOpen },
            { id: "tools", label: "Narzędzia", icon: Database },
        ],
    },
];

// Flat list (used for tab→component lookup and current label resolution).
const TABS = TAB_GROUPS.flatMap((g) => g.tabs);

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("stats");
    const { logoSrc } = useBrand();

    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg-base)] text-[color:var(--text-secondary)]">
                <Loader2 className="animate-spin" />
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="admin-dashboard">
            <header className="border-b border-white/10 bg-[color:var(--bg-surface)]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {logoSrc ? (
                            <img
                                src={logoSrc}
                                alt="Logo"
                                data-testid="admin-brand-logo"
                                className="h-9 w-auto max-w-[120px] object-contain"
                            />
                        ) : (
                            <span className="w-9 h-9 flex items-center justify-center rounded-sm border border-[color:var(--border-strong)] text-[color:var(--primary)] font-display font-bold">
                                PR
                            </span>
                        )}
                        <div>
                            <div className="font-display uppercase tracking-wider text-white text-sm">
                                Panel administratora
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)]">
                                {user.email}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href="/podglad-strony-404"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="admin-test-404"
                            className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] inline-flex items-center gap-1.5"
                            title="Otwiera podgląd strony błędu 404 w nowej karcie"
                        >
                            <AlertTriangle size={14} /> Test 404
                        </a>
                        <a
                            href="/500"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="admin-test-500"
                            className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)] hover:text-red-400 inline-flex items-center gap-1.5"
                            title="Otwiera podgląd strony błędu 500 w nowej karcie"
                        >
                            <AlertOctagon size={14} /> Test 500
                        </a>
                        <Link
                            to="/"
                            data-testid="admin-view-site"
                            className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)] hover:text-[color:var(--primary)] inline-flex items-center gap-1.5"
                        >
                            <HomeIcon size={14} /> Strona
                        </Link>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-secondary)] hover:text-red-400 inline-flex items-center gap-1.5"
                        >
                            <LogOut size={14} /> Wyloguj
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
                {/* Mobile / tablet: horizontal scrollable bar */}
                <div className="lg:hidden -mx-6 md:-mx-12 px-6 md:px-12 mb-6 overflow-x-auto border-b border-white/5">
                    <div className="flex gap-1 whitespace-nowrap">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`admin-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`px-4 py-3 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2 border-b-2 transition-colors shrink-0 ${
                                    tab === t.id
                                        ? "text-[color:var(--primary)] border-[color:var(--primary)]"
                                        : "text-[color:var(--text-secondary)] border-transparent hover:text-white"
                                }`}
                            >
                                <t.icon size={14} /> {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-10">
                    {/* Desktop sidebar */}
                    <aside
                        data-testid="admin-sidebar"
                        className="hidden lg:block sticky top-6 self-start"
                    >
                        <nav className="space-y-6">
                            {TAB_GROUPS.map((group) => (
                                <div key={group.id}>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] mb-2 px-3">
                                        {group.label}
                                    </div>
                                    <ul className="space-y-0.5">
                                        {group.tabs.map((t) => {
                                            const Icon = t.icon;
                                            const active = tab === t.id;
                                            return (
                                                <li key={t.id}>
                                                    <button
                                                        data-testid={`admin-tab-${t.id}`}
                                                        onClick={() => setTab(t.id)}
                                                        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md text-[12px] uppercase tracking-[0.18em] transition-colors ${
                                                            active
                                                                ? "bg-[color:var(--primary)]/10 text-[color:var(--primary)] border-l-2 border-[color:var(--primary)] -ml-[2px] pl-[14px]"
                                                                : "text-[color:var(--text-secondary)] hover:text-white hover:bg-white/[0.03]"
                                                        }`}
                                                    >
                                                        <Icon
                                                            size={14}
                                                            className="shrink-0"
                                                        />
                                                        <span className="truncate">
                                                            {t.label}
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </nav>
                    </aside>

                    {/* Content area */}
                    <div className="min-w-0">
                        <div className="hidden lg:flex items-baseline gap-3 mb-6 pb-3 border-b border-white/5">
                            {(() => {
                                const current = TABS.find((t) => t.id === tab);
                                const Icon = current?.icon;
                                return (
                                    <>
                                        {Icon && (
                                            <Icon
                                                size={18}
                                                className="text-[color:var(--primary)]"
                                            />
                                        )}
                                        <h2 className="font-display text-2xl text-white tracking-tight">
                                            {current?.label || "Panel"}
                                        </h2>
                                    </>
                                );
                            })()}
                        </div>

                        {tab === "stats" && <StatsManager />}
                        {tab === "plate-stats" && <PlateStatsManager />}
                        {tab === "reports" && <ReportsManager />}
                        {tab === "gallery" && <GalleryManager />}
                        {tab === "map" && <MapManager />}
                        {tab === "poi-categories" && <PoiCategoriesManager />}
                        {tab === "addons" && <AddonsManager />}
                        {tab === "archive" && <ArchiveManager />}
                        {tab === "team" && <TeamManager />}
                        {tab === "faq" && <FAQManager />}
                        {tab === "announcements" && <AnnouncementsManager />}
                        {tab === "social" && <SocialManager />}
                        {tab === "monitor" && <SystemMonitor />}
                        {tab === "settings" && <ModSettings />}
                        {tab === "media" && <MediaManager />}
                        {tab === "community" && <CommunityScreenshotsManager />}
                        {tab === "tools" && <ToolsManager />}
                    </div>
                </div>
            </div>
        </div>
    );
}
